import React, { useState, useEffect, useMemo } from "react";
import {
  Area,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
  ComposedChart,
} from "recharts";
import {
  fillLaborSummaryQuarterHours,
  get_t_str,
} from "../../../../helpers/helpers";
import CheckIcon from "@mui/icons-material/Check";
import PersonIcon from "@mui/icons-material/Person";
import MuiTooltip from "@mui/material/Tooltip";

const STAFFING_VARIANCE_TOLERANCE = 0.25;
const MAX_VISIBLE_ISSUES = 3;
const MAX_VISIBLE_SUPPORT_SEGMENTS = 6;
const NEEDED_LABOR_KEY = "Needed Labor Total";
const UNDER_VARIANCE_BASE_KEY = "Understaffed Variance Base";
const UNDER_VARIANCE_GAP_KEY = "Understaffed Variance Gap";
const OVER_VARIANCE_BASE_KEY = "Overstaffed Variance Base";
const OVER_VARIANCE_GAP_KEY = "Overstaffed Variance Gap";
const SUPPORT_AREA_BASE_KEY = "Support Area Base";
const SUPPORT_AREA_GAP_KEY = "Support Area Gap";
const HOVER_VARIANCE_BASE_KEY = "Hovered Variance Base";
const HOVER_VARIANCE_GAP_KEY = "Hovered Variance Gap";
const HOVER_SUPPORT_BASE_KEY = "Hovered Support Base";
const HOVER_SUPPORT_GAP_KEY = "Hovered Support Gap";
const PRIMARY_LABOR_COLOR = "#6d28d9";
const SERVICE_LABOR_COLOR = "#cbd5e1";
const SUPPORT_AREA_FILL = "#86efac";
const SUPPORT_LABOR_COLOR = "#bbf7d0";
const SUPPORT_AREA_HOVER_FILL = "#22c55e";
const SUPPORT_DETAIL_COLOR = "#16a34a";
const SERVICE_AREA_FILL_OPACITY = 0.46;
const SUPPORT_AREA_FILL_OPACITY = 0.24;
const SUPPORT_AREA_DISPLAY_FILL = `rgba(134, 239, 172, ${SUPPORT_AREA_FILL_OPACITY})`;
const SUPPORT_AREA_DISPLAY_BORDER = "rgba(134, 239, 172, 0.38)";
const DETAIL_ROW_BACKGROUND = "#f8fafc";
const DETAIL_ROW_BORDER = "#e2e8f0";
const SUPPORT_CHIP_BACKGROUND = "#dcfce7";
const SUPPORT_CHIP_HOVER_BACKGROUND = "#bbf7d0";
const SUPPORT_CHIP_BORDER = "#bbf7d0";
const NEEDED_AREA_FILL = "#cbd5e1";
const NEEDED_AREA_STROKE = "#b6c1cf";
const VARIANCE_FILL_ANIMATION_DELAY_MS = 450;
const VARIANCE_FILL_ANIMATION_DURATION_MS = 220;
const LEGEND_TOOLTIP_COMPONENT_PROPS = {
  tooltip: {
    sx: {
      backgroundColor: "white",
      border: "1px solid #e2e8f0",
      borderRadius: "8px",
      boxShadow:
        "0 20px 25px -5px rgb(0 0 0 / 0.1), 0 8px 10px -6px rgb(0 0 0 / 0.1)",
      color: "#334155",
      maxWidth: "380px",
      padding: "16px",
      width: "360px",
    },
  },
  arrow: {
    sx: {
      color: "white",
    },
  },
};

const toNumber = (value) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

const formatStaffingValue = (value) => {
  const rounded = Math.round(toNumber(value) * 10) / 10;
  return Number.isInteger(rounded) ? `${rounded}` : rounded.toFixed(1);
};

const getVarianceStatus = (difference) => {
  if (difference < -STAFFING_VARIANCE_TOLERANCE) {
    return "under";
  }
  if (difference > STAFFING_VARIANCE_TOLERANCE) {
    return "over";
  }
  return "balanced";
};

const statusLabelLookup = {
  under: "Under",
  over: "Over",
  balanced: "Balanced",
};

const statusStyleLookup = {
  under: {
    accent: "#2563eb",
    bgClass: "bg-blue-50",
    borderClass: "border-blue-100",
    textClass: "text-blue-700",
    chipBackground: "#eff6ff",
    chipHoverBackground: "#dbeafe",
    chipBorder: "#bfdbfe",
  },
  over: {
    accent: "#e11d48",
    bgClass: "bg-rose-50",
    borderClass: "border-rose-100",
    textClass: "text-rose-700",
    chipBackground: "#fff1f2",
    chipHoverBackground: "#ffe4e6",
    chipBorder: "#fecdd3",
  },
  balanced: {
    accent: "#059669",
    bgClass: "bg-emerald-50",
    borderClass: "border-emerald-100",
    textClass: "text-emerald-700",
    chipBackground: "#ecfdf5",
    chipHoverBackground: "#d1fae5",
    chipBorder: "#a7f3d0",
  },
};

const LegendTooltipContent = ({ title, description }) => (
  <div className="space-y-1">
    <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">
      {title}
    </div>
    <div className="text-sm leading-5 text-slate-700">{description}</div>
  </div>
);

const VisualCustomLineChart = (props) => {
  const [data, setData] = useState(props.data);
  const [hiddenLines, setHiddenLines] = useState([]);
  const [hoveredIssue, setHoveredIssue] = useState(null);
  const [hoveredSupportSegment, setHoveredSupportSegment] = useState(null);

  useEffect(() => {
    setData(props.data); // Update data state
  }, [props.data]);

  const getSupportColor = () => {
    return SUPPORT_AREA_FILL;
  };

  const handleCheckboxChange = (dataKey) => {
    setHoveredIssue(null);
    setHoveredSupportSegment(null);
    setHiddenLines((prevHiddenLines) => {
      if (prevHiddenLines.includes(dataKey)) {
        return prevHiddenLines.filter((key) => key !== dataKey);
      } else {
        return [...prevHiddenLines, dataKey];
      }
    });
  };

  const getCheckboxColor = (name) => {
    if (name === scheduledLaborKey) {
      return PRIMARY_LABOR_COLOR;
    }
    if (name === NEEDED_LABOR_KEY) {
      return NEEDED_AREA_FILL;
    }
    if (name.includes(`${props.label1} Service`)) {
      return SERVICE_LABOR_COLOR;
    }
    return SUPPORT_LABOR_COLOR;
  };

  const roleTypes = props.roleTypes;
  const scheduledLaborKey = `${props.label2} Labor Total`;
  const serviceLaborKey = `${props.label1} Service Total`;
  const supportLaborKey = `${props.label1} Support Total`;
  // The worked series has gone by both names. Match either, or renaming it
  // again silently turns these captions back into "Scheduled".
  const isWorkedSeries = props.label2 === "Used" || props.label2 === "Actual";
  const primaryLaborLabel = isWorkedSeries ? props.label2 : "Scheduled";
  const neededComponentLabel = props.label1 === "Earned" ? "Earned" : "Needed";
  const neededLaborLabel =
    props.label1 === "Earned" ? "Earned Staffing" : "Needed Staffing";
  const primaryLaborTooltip = isWorkedSeries
    ? "Labor actually worked during this day."
    : "Labor currently scheduled during this day.";
  const serviceLaborTooltip =
    props.label1 === "Earned"
      ? "Labor earned from core service demand. Service labor is more directly tied to sales demand and usually needs to happen during the same time window as that demand."
      : "Labor needed to cover core service demand. Service labor is more directly tied to sales demand and usually needs to happen during the same time window as that demand.";
  const supportLaborTooltip =
    props.label1 === "Earned"
      ? "Additional support labor earned on top of service. Support work is more flexible and may shift toward times with the most capacity. Service + support equals total earned staffing."
      : "Additional support labor needed on top of service. Support work is more flexible and may shift toward times with the most capacity. Service + support equals total needed staffing.";

  const wrapLegendTooltip = (children, title, description) => (
    <MuiTooltip
      arrow
      componentsProps={LEGEND_TOOLTIP_COMPONENT_PROPS}
      enterTouchDelay={0}
      placement="bottom"
      title={<LegendTooltipContent title={title} description={description} />}
    >
      {children}
    </MuiTooltip>
  );

  const showCheckBoxOption = (name, label, tooltipDescription) => {
    const isVisible = !hiddenLines.includes(name);
    const isNeededLineOption = name === NEEDED_LABOR_KEY && !props.isSmall;

    if (isNeededLineOption) {
      return wrapLegendTooltip(
        <button
          type="button"
          className={`flex items-center mt-1 bg-transparent p-0 text-slate-600 font-medium ${
            isVisible ? "" : "opacity-45"
          }`}
          onClick={() => handleCheckboxChange(name)}
          aria-pressed={isVisible}
        >
          <span
            className="inline-block h-3.5 w-8 mr-2 rounded-sm"
            style={{ backgroundColor: NEEDED_AREA_FILL }}
          />
          <span>{label}</span>
        </button>,
        label,
        tooltipDescription
      );
    }

    const legendControl = (
      <div className="flex items-center mt-1">
        <input
          type="checkbox"
          checked={isVisible}
          onChange={() => handleCheckboxChange(name)}
          style={{
            appearance: "none",
            WebkitAppearance: "none",
            width: props.isSmall ? "8px" : "14px",
            height: props.isSmall ? "8px" : "14px",
            borderRadius: "50px",
            border: `2px solid ${getCheckboxColor(name)}`,
            outline: "none",
            marginRight: "5px",
            cursor: "pointer",
            backgroundColor: isVisible ? getCheckboxColor(name) : "transparent",
          }}
        />
        <label htmlFor={name} className={`ml-1 ${props.isSmall && "text-xs"}`}>
          {label}
        </label>
      </div>
    );

    return tooltipDescription
      ? wrapLegendTooltip(legendControl, label, tooltipDescription)
      : legendControl;
  };

  const isNeededLayerVisible = (dataKey) =>
    !hiddenLines.includes(NEEDED_LABOR_KEY) && !hiddenLines.includes(dataKey);

  const showNeededAreaLegendOption = (
    dataKey,
    label,
    color,
    swatchStyle = {},
    tooltipDescription
  ) => {
    const isVisible = isNeededLayerVisible(dataKey);

    return wrapLegendTooltip(
      <button
        type="button"
        className={`flex items-center mt-1 bg-transparent p-0 text-slate-600 font-medium ${
          isVisible ? "" : "opacity-45"
        }`}
        onClick={() => handleCheckboxChange(dataKey)}
        aria-pressed={isVisible}
      >
        <span
          className="inline-block h-3.5 w-8 mr-2 rounded-sm"
          style={{ backgroundColor: color, ...swatchStyle }}
        />
        <span>{label}</span>
      </button>,
      label,
      tooltipDescription
    );
  };

  const showDataRow = (name, bold) => {
    return (
      <tr key={name}>
        <td
          className={
            "border px-5 py-4 text-xs whitespace-nowrap bg-gray-100 " + bold
          }
          style={{
            position: "sticky",
            left: 0,
            zIndex: 1,
            minWidth: "150px",
          }}
        >
          {name}
        </td>
        {data.map((entry, index) => (
          <td key={index} className="border">
            <div
              className={
                "h-full w-full flex items-center justify-center text-sm m-auto " +
                bold
              }
            >
              <p>{entry[name] ? entry[name] : 0}</p>
            </div>
          </td>
        ))}
      </tr>
    );
  };

  const showLine = (name) => {
    return (
      !hiddenLines.includes(name) && (
        <Line
          type="stepAfter"
          dataKey={name}
          stroke={getCheckboxColor(name)}
          strokeWidth={
            name === scheduledLaborKey ? 2.5 : name === NEEDED_LABOR_KEY ? 1.2 : 2
          }
          // dot={{ onMouseDown: (event, payload) => event.stopPropagation() }}
          isAnimationActive={name !== scheduledLaborKey}
          dot={false}
        />
      )
    );
  };

  const showVarianceArea = (baseKey, gapKey, color, stackId, options = {}) => {
    const {
      fillOpacity,
      isAnimationActive = true,
      animationBegin = VARIANCE_FILL_ANIMATION_DELAY_MS,
      animationDuration = VARIANCE_FILL_ANIMATION_DURATION_MS,
      requiresScheduled = true,
      requiredVisibleKeys = [serviceLaborKey, supportLaborKey],
    } = options;
    const shouldShow =
      !props.isSmall &&
      !hiddenLines.includes(NEEDED_LABOR_KEY) &&
      (!requiresScheduled || !hiddenLines.includes(scheduledLaborKey)) &&
      requiredVisibleKeys.every((key) => !hiddenLines.includes(key));

    return (
      shouldShow && (
        <>
          <Area
            type="stepAfter"
            dataKey={baseKey}
            stackId={stackId}
            fill="transparent"
            stroke="transparent"
            isAnimationActive={false}
            activeDot={false}
          />
          <Area
            type="stepAfter"
            dataKey={gapKey}
            stackId={stackId}
            fill={color}
            fillOpacity={fillOpacity}
            stroke="transparent"
            isAnimationActive={isAnimationActive}
            animationBegin={animationBegin}
            animationDuration={animationDuration}
            animationEasing="ease-out"
            activeDot={false}
          />
        </>
      )
    );
  };

  const showNeededArea = () => {
    return (
      !props.isSmall &&
      isNeededLayerVisible(serviceLaborKey) && (
        <Area
          type="stepAfter"
          dataKey={serviceLaborKey}
          fill={NEEDED_AREA_FILL}
          fillOpacity={SERVICE_AREA_FILL_OPACITY}
          stroke={NEEDED_AREA_STROKE}
          strokeOpacity={0.55}
          strokeWidth={1}
          isAnimationActive={false}
          activeDot={false}
        />
      )
    );
  };

  const showSupportArea = () => {
    return showVarianceArea(
      SUPPORT_AREA_BASE_KEY,
      SUPPORT_AREA_GAP_KEY,
      SUPPORT_AREA_FILL,
      "supportArea",
      {
        fillOpacity: SUPPORT_AREA_FILL_OPACITY,
        isAnimationActive: false,
        animationBegin: 0,
        animationDuration: 0,
        requiresScheduled: false,
        requiredVisibleKeys: [supportLaborKey],
      }
    );
  };

  const showStackedArea = (name) => {
    const isSupportBreakdown = props.supportNames?.some(
      (role) => name === `${role} Support`
    );
    const isSupportArea = isSupportBreakdown || name === supportLaborKey;
    const isServiceArea = name === serviceLaborKey;
    const color = isSupportArea ? getSupportColor(name) : getCheckboxColor(name);

    return (
      !hiddenLines.includes(NEEDED_LABOR_KEY) && (
        <Area
          type="stepAfter"
          dataKey={name}
          fill={color}
          fillOpacity={
            isSupportArea
              ? SUPPORT_AREA_FILL_OPACITY
              : isServiceArea
              ? SERVICE_AREA_FILL_OPACITY
              : 0.28
          }
          stroke={isSupportArea ? "transparent" : color}
          stackId="1"
        />
      )
    );
  };

  const supportNames = useMemo(
    () => props.supportNames || [],
    [props.supportNames]
  );
  const supportKeys =
    supportNames.length
      ? supportNames.map((n) => `${n} Support`)
      : [supportLaborKey];

  const CustomTooltip = ({ active, payload, label, coordinate, viewBox }) => {
    if (active && payload && payload.length) {
      const tooltipRow = payload[0]?.payload || {};
      const isServiceVisible = isNeededLayerVisible(serviceLaborKey);
      const isSupportVisible = isNeededLayerVisible(supportLaborKey);
      const hasVisibleNeeded = isServiceVisible || isSupportVisible;
      const serviceTotal = isServiceVisible
        ? toNumber(tooltipRow?.[serviceLaborKey])
        : 0;
      const aggregateSupportTotal = isSupportVisible
        ? toNumber(tooltipRow?.[supportLaborKey])
        : 0;
      const namedSupportTotal = supportKeys.reduce(
        (sum, key) => sum + (isSupportVisible ? toNumber(tooltipRow?.[key]) : 0),
        0
      );
      const supportTotal =
        aggregateSupportTotal > 0 ? aggregateSupportTotal : namedSupportTotal;
      const scheduledEntry = payload.find((e) => e.name === scheduledLaborKey);
      const hasVisibleScheduled =
        !hiddenLines.includes(scheduledLaborKey) && Boolean(scheduledEntry);
      const scheduledTotal = hasVisibleScheduled
        ? toNumber(scheduledEntry.value)
        : 0;
      const forecastedTotal = serviceTotal + supportTotal;
      const difference = scheduledTotal - forecastedTotal;
      const supportBreakdownRows = isSupportVisible
        ? supportNames
            .map((name) => ({
              key: `${name} Support`,
              label: name,
              value: toNumber(tooltipRow?.[`${name} Support`]),
            }))
            .filter((row) => row.value > 0)
        : [];
      const showSupportTotalRow =
        isSupportVisible && (supportTotal > 0 || supportBreakdownRows.length > 0);
      const showStaffingDelta = hasVisibleNeeded && hasVisibleScheduled;

      // Calculate tooltip positioning
      const tooltipWidth = 320; // Approximate width of the tooltip
      let left = coordinate ? coordinate.x : 0;
      let transform = "translateX(-50%)";

      // Adjust for left edge
      if (coordinate && coordinate.x - tooltipWidth / 2 < 0) {
        left = tooltipWidth / 2;
        transform = "translateX(-50%)";
      }

      // Adjust for right edge
      if (
        viewBox &&
        coordinate &&
        coordinate.x + tooltipWidth / 2 > viewBox.width
      ) {
        left = viewBox.width - tooltipWidth / 2;
        transform = "translateX(-50%)";
      }

      return (
        <div
          className="bg-white border border-gray-300 w-80 px-6 py-5 rounded-xl shadow-xl"
          style={{
            position: "absolute",
            top: props.guestReviews ? "-200px" : (props.type === "daily" ? "-262px" : "-150px"), // Lower for guest reviews
            left: `${left}px`,
            transform: transform,
            zIndex: 1000,
            width: "320px", // Fixed width to prevent resizing
            boxSizing: "border-box",
          }}
        >
          <div className="w-full flex items-center justify-between mb-2">
            <p className="text-slate-900 text-sm">{`${label}`}</p>
            <p className="text-slate-900 text-sm">Total</p>
          </div>
          {hasVisibleNeeded && (
            <div className="w-full flex items-center justify-between py-2">
              <div className="flex items-center space-x-3">
                <div
                  className="w-2.5 h-2.5 rounded-sm"
                  style={{ backgroundColor: NEEDED_AREA_FILL }}
                ></div>
                <div className="text-slate-500 text-sm">{neededLaborLabel}</div>
              </div>
              <div className="text-slate-900 text-sm">
                {formatStaffingValue(forecastedTotal)}
              </div>
            </div>
          )}
          {isServiceVisible && serviceTotal > 0 && (
            <div className="w-full flex items-center justify-between py-1.5 pl-5">
              <div className="flex items-center space-x-3">
                <div
                  className="w-2 h-2 rounded-sm"
                  style={{ backgroundColor: NEEDED_AREA_FILL }}
                ></div>
                <div className="text-slate-500 text-xs">Service</div>
              </div>
              <div className="text-slate-700 text-xs">
                {formatStaffingValue(serviceTotal)}
              </div>
            </div>
          )}
          {showSupportTotalRow && (
            <div className="w-full flex items-center justify-between py-1.5 pl-5">
              <div className="flex items-center space-x-3">
                <div
                  className="w-2 h-2 rounded-sm"
                  style={{
                    backgroundColor: SUPPORT_AREA_DISPLAY_FILL,
                    border: `1px solid ${SUPPORT_AREA_DISPLAY_BORDER}`,
                  }}
                ></div>
                <div className="text-slate-500 text-xs">Support</div>
              </div>
              <div className="text-slate-700 text-xs">
                {formatStaffingValue(supportTotal)}
              </div>
            </div>
          )}
          {supportBreakdownRows.map((row) => (
            <div
              key={row.key}
              className="w-full flex items-center justify-between py-1 pl-10"
            >
              <div className="text-slate-400 text-xs">{row.label}</div>
              <div className="text-slate-500 text-xs">
                {formatStaffingValue(row.value)}
              </div>
            </div>
          ))}
          {hasVisibleScheduled && scheduledTotal > 0 && (
            <div className="w-full flex items-center justify-between border-t border-slate-200 py-2 mt-2">
              <div className="flex items-center space-x-3">
                <div
                  className="w-2.5 h-2.5 rounded-full"
                  style={{ backgroundColor: PRIMARY_LABOR_COLOR }}
                ></div>
                <div className="text-slate-500 text-sm">
                  {primaryLaborLabel}
                </div>
              </div>
              <div className="text-slate-900 text-sm">
                {formatStaffingValue(scheduledTotal)}
              </div>
            </div>
          )}
          {showStaffingDelta && (
            difference === 0 ? (
              <div className="mt-1 flex flex-col items-center justify-center text-emerald-600">
                <div>
                  <CheckIcon style={{ fontSize: "32px" }} />
                </div>
                <div className="text-sm">Perfect</div>
              </div>
            ) : difference < 0 ? (
              <div className="mt-1 flex flex-col items-center justify-center text-blue-600">
                <div className="flex items-center space-x-1">
                  <div className="font-bold text-2xl">{Math.abs(difference)}</div>
                  <div>
                    <PersonIcon
                      style={{ fontSize: "26px", marginBottom: "1px" }}
                    />
                  </div>
                </div>
                <div className="text-sm">Understaffed</div>
              </div>
            ) : (
              <div className="mt-1 flex flex-col items-center justify-center text-rose-600">
                <div className="flex items-center space-x-1">
                  <div className="font-bold text-2xl">{Math.abs(difference)}</div>
                  <div>
                    <PersonIcon
                      style={{ fontSize: "26px", marginBottom: "1px" }}
                    />
                  </div>
                </div>
                <div className="text-sm">Overstaffed</div>
              </div>
            )
          )}
        </div>
      );
    }
    return null;
  };

  const rangePaddedData = fillLaborSummaryQuarterHours(
    data,
    get_t_str,
    props.label1,
    props.label2,
    props.start_h,
    props.end_h,
    supportNames
  );
  const hasChartRange =
    Number.isFinite(props.start_h) &&
    Number.isFinite(props.end_h) &&
    props.end_h > props.start_h;
  const rawChartData = hasChartRange ? rangePaddedData : data;
  const isServiceLayerVisible = isNeededLayerVisible(serviceLaborKey);
  const chartData = useMemo(
    () =>
      (rawChartData || []).map((row) => {
        const service = toNumber(row?.[serviceLaborKey]);
        const support = toNumber(row?.[supportLaborKey]);
        const needed = service + support;
        const scheduled = toNumber(row?.[scheduledLaborKey]);
        const hasStaffing = scheduled > 0 || needed > 0;
        const difference = scheduled - needed;
        const status = hasStaffing ? getVarianceStatus(difference) : "balanced";
        const rowQuarterHour = Number(row?.qh_num);
        const isHoveredBucket =
          !props.isSmall &&
          hoveredIssue &&
          Number.isFinite(rowQuarterHour) &&
          rowQuarterHour >= hoveredIssue.startQh &&
          rowQuarterHour < hoveredIssue.endQh &&
          status === hoveredIssue.status;
        let hoverVarianceBase = 0;
        if (isHoveredBucket && status === "under") {
          hoverVarianceBase = scheduled;
        } else if (isHoveredBucket && status === "over") {
          hoverVarianceBase = needed;
        }
        const isHoveredSupportBucket =
          !props.isSmall &&
          hoveredSupportSegment &&
          Number.isFinite(rowQuarterHour) &&
          rowQuarterHour >= hoveredSupportSegment.startQh &&
          rowQuarterHour < hoveredSupportSegment.endQh;
        const hoveredSupportValue = isHoveredSupportBucket
          ? toNumber(row?.[hoveredSupportSegment.key])
          : 0;
        const visibleSupportBase = isServiceLayerVisible ? service : 0;

        return {
          ...row,
          [NEEDED_LABOR_KEY]: needed,
          [UNDER_VARIANCE_BASE_KEY]: status === "under" ? scheduled : 0,
          [UNDER_VARIANCE_GAP_KEY]:
            status === "under" ? Math.abs(difference) : 0,
          [OVER_VARIANCE_BASE_KEY]: status === "over" ? needed : 0,
          [OVER_VARIANCE_GAP_KEY]:
            status === "over" ? Math.abs(difference) : 0,
          [SUPPORT_AREA_BASE_KEY]: support > 0 ? visibleSupportBase : 0,
          [SUPPORT_AREA_GAP_KEY]: support,
          [HOVER_VARIANCE_BASE_KEY]: hoverVarianceBase,
          [HOVER_VARIANCE_GAP_KEY]: isHoveredBucket ? Math.abs(difference) : 0,
          [HOVER_SUPPORT_BASE_KEY]: isHoveredSupportBucket
            ? visibleSupportBase
            : 0,
          [HOVER_SUPPORT_GAP_KEY]: hoveredSupportValue,
        };
      }),
    [
      rawChartData,
      scheduledLaborKey,
      serviceLaborKey,
      supportLaborKey,
      isServiceLayerVisible,
      hoveredIssue,
      hoveredSupportSegment,
      props.isSmall,
    ]
  );
  const lastXAxisLabel = chartData.length
    ? chartData[chartData.length - 1].time
    : null;
  const canShowStaffingDeltas =
    !props.isSmall &&
    !hiddenLines.includes(scheduledLaborKey) &&
    isNeededLayerVisible(serviceLaborKey) &&
    isNeededLayerVisible(supportLaborKey);
  const canShowSupportDetails =
    !props.isSmall && isNeededLayerVisible(supportLaborKey);

  const staffingSummary = useMemo(() => {
    if (!canShowStaffingDeltas) {
      return {
        visibleIssues: [],
      };
    }

    const summaryRows =
      hasChartRange && chartData.length > 1 ? chartData.slice(0, -1) : chartData;

    const buckets = summaryRows
      .map((row, index) => {
        const parsedStartQh = Number(row?.qh_num);
        const startQh = Number.isFinite(parsedStartQh)
          ? parsedStartQh
          : index * 0.25;
        const nextQh = Number(chartData[index + 1]?.qh_num);
        const endQh =
          Number.isFinite(nextQh) && nextQh > startQh ? nextQh : startQh + 0.25;
        const duration = Math.max(endQh - startQh, 0.25);
        const scheduled = toNumber(row?.[scheduledLaborKey]);
        const service = toNumber(row?.[serviceLaborKey]);
        const support = toNumber(row?.[supportLaborKey]);
        const needed = service + support;
        const difference = scheduled - needed;
        const status = getVarianceStatus(difference);

        return {
          row,
          scheduled,
          needed,
          difference,
          status,
          startQh,
          endQh,
          duration,
        };
      })
      .filter(
        (bucket) =>
          bucket.duration > 0 && (bucket.scheduled > 0 || bucket.needed > 0)
      );

    const segments = [];
    let currentSegment = null;

    const closeCurrentSegment = () => {
      if (currentSegment) {
        segments.push({
          ...currentSegment,
          startTime: get_t_str(currentSegment.startQh),
          endTime: get_t_str(currentSegment.endQh),
        });
        currentSegment = null;
      }
    };

    buckets.forEach((bucket) => {
      if (bucket.status === "balanced") {
        closeCurrentSegment();
        return;
      }

      const magnitude = Math.abs(bucket.difference);
      const canExtend =
        currentSegment &&
        currentSegment.status === bucket.status &&
        Math.abs(currentSegment.endQh - bucket.startQh) < 0.001;

      if (canExtend) {
        currentSegment.endQh = bucket.endQh;
        currentSegment.peak = Math.max(currentSegment.peak, magnitude);
        currentSegment.total += magnitude * bucket.duration;
      } else {
        closeCurrentSegment();
        currentSegment = {
          status: bucket.status,
          startQh: bucket.startQh,
          endQh: bucket.endQh,
          peak: magnitude,
          total: magnitude * bucket.duration,
        };
      }
    });
    closeCurrentSegment();

    const sortedBySeverity = [...segments].sort((a, b) => {
      if (b.peak !== a.peak) {
        return b.peak - a.peak;
      }
      return b.total - a.total;
    });
    const visibleIssues = sortedBySeverity
      .slice(0, MAX_VISIBLE_ISSUES)
      .sort((a, b) => a.startQh - b.startQh);

    return {
      visibleIssues,
    };
  }, [
    chartData,
    canShowStaffingDeltas,
    hasChartRange,
    scheduledLaborKey,
    serviceLaborKey,
    supportLaborKey,
  ]);

  const formatIssueRange = (issue) => `${issue.startTime}-${issue.endTime}`;
  const formatSupportRange = (segment) =>
    `${segment.startTime}-${segment.endTime}`;
  const getIssueCopy = (issue) => {
    if (!issue) {
      return "Coverage balanced";
    }
    return `${statusLabelLookup[issue.status]} by ${formatStaffingValue(
      issue.peak
    )}`;
  };
  const getIssueKey = (issue) =>
    `${issue.status}-${issue.startQh}-${issue.endQh}`;
  const getSupportSegmentKey = (segment) =>
    `${segment.key}-${segment.startQh}-${segment.endQh}`;

  const supportSegments = useMemo(() => {
    if (!canShowSupportDetails) {
      return [];
    }

    const summaryRows =
      hasChartRange && chartData.length > 1 ? chartData.slice(0, -1) : chartData;
    const supportDetails = supportNames.length
      ? supportNames.map((name) => ({
          key: `${name} Support`,
          label: name,
        }))
      : [
          {
            key: supportLaborKey,
            label: "Support",
          },
        ];
    const segments = [];

    supportDetails.forEach((supportDetail, laneIndex) => {
      let currentSegment = null;

      const closeCurrentSegment = () => {
        if (currentSegment) {
          segments.push({
            ...currentSegment,
            startTime: get_t_str(currentSegment.startQh),
            endTime: get_t_str(currentSegment.endQh),
          });
          currentSegment = null;
        }
      };

      summaryRows.forEach((row, index) => {
        const parsedStartQh = Number(row?.qh_num);
        const startQh = Number.isFinite(parsedStartQh)
          ? parsedStartQh
          : index * 0.25;
        const nextQh = Number(chartData[index + 1]?.qh_num);
        const endQh =
          Number.isFinite(nextQh) && nextQh > startQh ? nextQh : startQh + 0.25;
        const duration = Math.max(endQh - startQh, 0.25);
        const supportValue = toNumber(row?.[supportDetail.key]);

        if (supportValue <= 0) {
          closeCurrentSegment();
          return;
        }

        const canExtend =
          currentSegment && Math.abs(currentSegment.endQh - startQh) < 0.001;

        if (canExtend) {
          currentSegment.endQh = endQh;
          currentSegment.totalHours += supportValue * duration;
          currentSegment.peak = Math.max(currentSegment.peak, supportValue);
        } else {
          closeCurrentSegment();
          currentSegment = {
            ...supportDetail,
            laneIndex,
            startQh,
            endQh,
            totalHours: supportValue * duration,
            peak: supportValue,
          };
        }
      });

      closeCurrentSegment();
    });

    return segments.sort((a, b) => {
      if (a.startQh !== b.startQh) {
        return a.startQh - b.startQh;
      }
      return a.label.localeCompare(b.label);
    });
  }, [
    canShowSupportDetails,
    chartData,
    hasChartRange,
    supportLaborKey,
    supportNames,
  ]);

  const visibleSupportSegments = supportSegments.slice(
    0,
    MAX_VISIBLE_SUPPORT_SEGMENTS
  );
  const chartMaxLabor = Math.max(
    1,
    ...chartData.map((row) =>
      Math.max(toNumber(row?.[scheduledLaborKey]), toNumber(row?.[NEEDED_LABOR_KEY]))
    )
  );
  const chartMaxY = Math.ceil(chartMaxLabor + 1);

  const fillsHeight = !props.isSmall && props.height === "100%";
  const hasDeltaChips =
    canShowStaffingDeltas && staffingSummary.visibleIssues.length > 0;
  const hasSupportChips = canShowSupportDetails && supportSegments.length > 0;

  return (
    <div
      className={`relative ${fillsHeight ? "flex flex-col min-h-0" : ""}`}
    >
      {!props.isSmall && (
        <div className="ml-6 mr-16 shrink-0">
          <div className="flex items-center justify-between gap-6">
            <div className="min-w-0">
              <div className="flex items-center gap-3">
                <h2 className="text-lg font-semibold text-slate-900">
                  {primaryLaborLabel} vs {neededLaborLabel}
                </h2>
                {props.type === "daily" && props.gradeLetter && (
                  <div
                    style={{ backgroundColor: props.gradeColor }}
                    className="w-7 h-7 rounded-lg flex items-center justify-center text-white"
                  >
                    <p className="text-sm font-semibold">{props.gradeLetter}</p>
                  </div>
                )}
              </div>
              {props.date_name && (
                <h3 className="text-slate-500 text-sm mt-0.5">
                  {props.date_name}
                </h3>
              )}
            </div>
            <div className="text-sm font-medium text-slate-600 flex flex-wrap items-center justify-end gap-x-6 gap-y-1">
              <div>
                {showCheckBoxOption(
                  scheduledLaborKey,
                  primaryLaborLabel,
                  primaryLaborTooltip
                )}
              </div>
              <div>
                {showNeededAreaLegendOption(
                  serviceLaborKey,
                  `${neededComponentLabel} Service`,
                  NEEDED_AREA_FILL,
                  {},
                  serviceLaborTooltip
                )}
              </div>
              <div>
                {showNeededAreaLegendOption(
                  supportLaborKey,
                  `${neededComponentLabel} Support`,
                  SUPPORT_AREA_DISPLAY_FILL,
                  { border: `1px solid ${SUPPORT_AREA_DISPLAY_BORDER}` },
                  supportLaborTooltip
                )}
              </div>
            </div>
          </div>

          {hasDeltaChips && (
            <div
              className="mt-2 rounded-lg border px-3 py-1.5 flex flex-wrap items-center gap-x-2 gap-y-1.5"
              style={{
                backgroundColor: DETAIL_ROW_BACKGROUND,
                borderColor: DETAIL_ROW_BORDER,
              }}
            >
              <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-500 mr-1">
                Largest gaps
              </p>
                  {staffingSummary.visibleIssues.map((issue) => {
                    const style = statusStyleLookup[issue.status];
                    const issueKey = getIssueKey(issue);
                    const isHovered =
                      hoveredIssue && getIssueKey(hoveredIssue) === issueKey;
                    return (
                      <button
                        type="button"
                        key={issueKey}
                        className="flex items-center gap-1.5 rounded-md border px-2 py-1 text-xs transition-colors"
                        onMouseEnter={() => setHoveredIssue(issue)}
                        onMouseLeave={() => setHoveredIssue(null)}
                        onFocus={() => setHoveredIssue(issue)}
                        onBlur={() => setHoveredIssue(null)}
                        style={{
                          backgroundColor: isHovered
                            ? style.chipHoverBackground
                            : style.chipBackground,
                          borderColor: isHovered
                            ? style.accent
                            : style.chipBorder,
                          boxShadow: isHovered
                            ? `0 0 0 1px ${style.accent}22`
                            : undefined,
                        }}
                      >
                        <span
                          className="w-1.5 h-1.5 rounded-full"
                          style={{ backgroundColor: style.accent }}
                        />
                        <span className={`font-semibold ${style.textClass}`}>
                          {getIssueCopy(issue)}
                        </span>
                        <span className="text-slate-500">
                          {formatIssueRange(issue)}
                        </span>
                      </button>
                    );
                  })}
            </div>
          )}

          {hasSupportChips && (
            <div
              className="mt-2 rounded-lg border px-3 py-1.5 flex flex-wrap items-center gap-x-2 gap-y-1.5"
              style={{
                backgroundColor: DETAIL_ROW_BACKGROUND,
                borderColor: DETAIL_ROW_BORDER,
              }}
            >
              <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-500 mr-1">
                Support work
              </p>
                  {visibleSupportSegments.map((segment) => {
                    const segmentKey = getSupportSegmentKey(segment);
                    const isHovered =
                      hoveredSupportSegment &&
                      getSupportSegmentKey(hoveredSupportSegment) === segmentKey;
                    return (
                      <button
                        type="button"
                        key={segmentKey}
                        className="flex items-center gap-1.5 rounded-md border px-2 py-1 text-xs transition-colors"
                        onMouseEnter={() => setHoveredSupportSegment(segment)}
                        onMouseLeave={() => setHoveredSupportSegment(null)}
                        onFocus={() => setHoveredSupportSegment(segment)}
                        onBlur={() => setHoveredSupportSegment(null)}
                        style={{
                          borderColor: isHovered
                            ? SUPPORT_DETAIL_COLOR
                            : SUPPORT_CHIP_BORDER,
                          backgroundColor: isHovered
                            ? SUPPORT_CHIP_HOVER_BACKGROUND
                            : SUPPORT_CHIP_BACKGROUND,
                          boxShadow: isHovered
                            ? `0 0 0 1px ${SUPPORT_DETAIL_COLOR}22`
                            : undefined,
                        }}
                      >
                        <span
                          className="w-1.5 h-1.5 rounded-full"
                          style={{ backgroundColor: SUPPORT_DETAIL_COLOR }}
                        />
                        {segment.label !== "Support" && (
                          <span className="font-semibold text-emerald-700">
                            {segment.label}
                          </span>
                        )}
                        <span className="text-slate-500">
                          {formatStaffingValue(segment.totalHours)} hrs
                        </span>
                        <span className="text-slate-500">
                          {formatSupportRange(segment)}
                        </span>
                      </button>
                    );
                  })}
              {supportSegments.length > visibleSupportSegments.length && (
                <span className="text-xs text-slate-500">
                  +{supportSegments.length - visibleSupportSegments.length}{" "}
                  more
                </span>
              )}
            </div>
          )}
        </div>
      )}
      <div
        className={`${
          props.isSmall
            ? props.guestReviews
              ? "-ml-[26px] -mr-[10px] -mt-[5px] -mb-[8px]"
              : "-ml-[23px] -mr-[5px]"
            : fillsHeight
            ? "mt-3 min-h-0"
            : "mt-5"
        }`}
        style={fillsHeight ? { flex: "0 1 440px", minHeight: 240 } : undefined}
      >
        {
          <ResponsiveContainer
            width="100%"
            height={fillsHeight ? "100%" : props.height}
          >
            <ComposedChart data={chartData}>
              <XAxis
                dataKey="time"
                tick={{ fontSize: 12 }}
                tickLine={false}
                interval={3}
                minTickGap={20}
                tickFormatter={(value) =>
                  value === lastXAxisLabel ? "" : value
                }
                hide={props.isSmall ? true : false}
              />
              <YAxis
                tick={
                  props.isSmall
                    ? { fontSize: 11, fill: "#94a3b8" }
                    : { fontSize: props.guestReviews ? 0 : 14 }
                }
                tickLine={false}
                axisLine={!props.isSmall}
                width={20}
                allowDecimals={false}
                domain={props.isSmall ? undefined : [0, chartMaxY]}
              />
              {props.isSmall ? (
                <CartesianGrid stroke={props.gridColor || "#e2e8f0"} />
              ) : (
                <CartesianGrid stroke="#eee" strokeDasharray="5 5" />
              )}

              {props.isSmall ? (
                <>
                  {showStackedArea(serviceLaborKey)}
                  {supportNames.length
                    ? supportNames.map((n) => showStackedArea(`${n} Support`))
                    : showStackedArea(supportLaborKey)}
                </>
              ) : (
                <>
                  {showNeededArea()}
                  {showSupportArea()}
                  {canShowSupportDetails &&
                    hoveredSupportSegment &&
                    showVarianceArea(
                      HOVER_SUPPORT_BASE_KEY,
                      HOVER_SUPPORT_GAP_KEY,
                      SUPPORT_AREA_HOVER_FILL,
                      "hoverSupport",
                      {
                        fillOpacity: 0.44,
                        isAnimationActive: false,
                        animationBegin: 0,
                        animationDuration: 0,
                        requiresScheduled: false,
                        requiredVisibleKeys: [supportLaborKey],
                      }
                    )}
                  {canShowStaffingDeltas &&
                    hoveredIssue &&
                    showVarianceArea(
                      HOVER_VARIANCE_BASE_KEY,
                      HOVER_VARIANCE_GAP_KEY,
                      hoveredIssue.status === "under" ? "#93c5fd" : "#fda4af",
                      "hoverVariance",
                      {
                        fillOpacity: 0.46,
                        isAnimationActive: false,
                        animationBegin: 0,
                        animationDuration: 0,
                      }
                    )}
                </>
              )}
              {showLine(scheduledLaborKey)}
              <RechartsTooltip content={<CustomTooltip />} />
            </ComposedChart>
          </ResponsiveContainer>
        }
      </div>
      {props.isTable && (
        <div>
          <h3 className="ml-12 mt-3 font-bold">{`${props.label1} Staffing Table`}</h3>
          <div className="mt-3 mr-12 ml-12">
            <div
              className="flex flex-wrap items-center gap-5 overflow-scroll w-full"
              style={{
                boxShadow: "inset -10px 0px 10px -10px rgba(0, 0, 0, 0.5)",
              }}
            >
              <table className="table-auto border-collapse">
                <thead>
                  <tr>
                    <th
                      className="border px-5 py-3 bg-primary text-white"
                      style={{
                        position: "sticky",
                        left: 0,
                        zIndex: 1,
                        minWidth: "150px",
                      }}
                    ></th>
                    {data.map((entry, index) => (
                      <th
                        key={index}
                        className="border px-5 py-2 text-xs bg-primary text-white"
                      >
                        {entry.time}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {showDataRow(`${props.label1} Service Total`, "font-bold")}
                  {roleTypes.map((roleType) =>
                    showDataRow(`${roleType} ${props.label1} Service`, "")
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default VisualCustomLineChart;
